import { FC, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import SocketContext from "./socketContext";
import { backendUrl } from "config";
import { useAppDispatch, useAppSelector } from "store";
import { setLastDate } from "store/slices/orderSlice";
import { useQueryClient } from "@tanstack/react-query";
import {
  CREDIT_BALANCE_SOCKET_RELOAD_MSG,
  ORDER_PAYMENT_SOCKET_RELOAD_MSG,
  PARCEL_LIST_REFRESH_EVENT,
  PARCEL_LIST_SOCKET_RELOAD_MSG,
  PARCEL_TRACKING_SOCKET_RELOAD_MSG,
} from "components/ManageParcel/ParcelShared/parcelListEvents";
import {
  CREDIT_BALANCE_QUERY_KEY,
  COD_CREDIT_INFO_QUERY_KEY,
  ACTIVE_CREDIT_LOTS_QUERY_KEY,
} from "hooks/useFactoryCredit";
import { CREDIT_TRANSACTIONS_QUERY_KEY } from "hooks/useFactoryCreditTransactions";

interface SocketContextProps {
  children: JSX.Element;
}

const SocketProvider: FC<SocketContextProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { accessToken } = useAppSelector((state) => state.auth);
  const queryClient = useQueryClient();

  const socket = useRef(
    io(backendUrl || "", {
      transports: ["websocket"],
      auth: {
        token: `${accessToken}`,
      },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
    })
  );

  useEffect(() => {
    const currentSocket = socket.current;

    currentSocket.on("connect", () => {
      console.log("SocketIO: Connected and authenticated");
    });

    currentSocket.on("error", (msg: string) => {
      console.error("SocketIO: Error", msg);
    });

    currentSocket.on("reload", async (msg: string) => {
      console.log("SocketIO: Reload", msg);

      // ต้องรอ list จบก่อนค่อยบอก modal อัปเดตยอด — เดิม fire-and-forget ทำให้
      // "ยอดชำระเงินรวม" อ่าน cache เก่ายังเป็น 0 จนกว่าจะกด refresh
      await Promise.all([
        queryClient.refetchQueries({
          queryKey: ["orderDataNewOrder"],
          type: "active",
        }),
        queryClient.refetchQueries({
          queryKey: ["getCompleteOrder"],
          type: "active",
        }),
        queryClient.refetchQueries({
          queryKey: ["getNotificationList"],
          type: "active",
        }),
      ]);

      if (msg === "tracking generated" || msg === "self tracking saved") {
        queryClient.refetchQueries({
          queryKey: ["orderTracks"],
          type: "active",
        });
        // แจ้งให้ TrackingNumberPanel ดึงข้อมูลใหม่
        window.dispatchEvent(new Event("forceRefreshTrackingPanel"));
        // แจ้งให้ ShippingResultModal โหลดข้อมูลใหม่ (ถ้าเปิดอยู่)
        window.dispatchEvent(new Event("forceRefreshShippingModal"));
        console.log("msg", msg);
      }

      // ยอดชำระใน ModalOrderDetail เป็น local state — ต้องยิง event ทุก reload ที่เกี่ยวกับ
      // tracking/COD/เครดิต ไม่ใช่แค่ "tracking generated" ไม่งั้น webhook "ได้รับเงินแล้ว"
      // ที่มาคู่กับ "factory credit balance updated" จะไม่รีเฟรชยอดจนกว่าจะกด refresh
      if (
        msg === "tracking generated" ||
        msg === "self tracking saved" ||
        msg === ORDER_PAYMENT_SOCKET_RELOAD_MSG ||
        msg === CREDIT_BALANCE_SOCKET_RELOAD_MSG
      ) {
        window.dispatchEvent(new Event("forceRefreshOrderPayment"));
        window.dispatchEvent(new Event("forceRefreshTrackingPanel"));
      }

      if (
        msg === PARCEL_LIST_SOCKET_RELOAD_MSG ||
        msg === PARCEL_TRACKING_SOCKET_RELOAD_MSG
      ) {
        window.dispatchEvent(new Event(PARCEL_LIST_REFRESH_EVENT));
      }

      if (msg === CREDIT_BALANCE_SOCKET_RELOAD_MSG) {
        // ไม่จำกัด type: "active" — การ์ดเครดิต / ตารางทุกแท็บต้องขยับทันทีหลังเติมหรือ webhook เก็บเงินได้
        await Promise.all([
          queryClient.refetchQueries({ queryKey: [CREDIT_BALANCE_QUERY_KEY] }),
          queryClient.refetchQueries({ queryKey: [COD_CREDIT_INFO_QUERY_KEY] }),
          // modal "เครดิตที่ใช้ได้" (ExpiringCreditModal) ใช้ query key แยกต่างหาก — เดิมไม่อยู่ในลิสต์นี้
          // เลย ทำให้ยอดรวม/การ์ดหลักอัปเดตสดถูกต้องหลัง webhook แต่ modal ยังค้าง cache เก่าจนกว่าจะปิด-
          // เปิดใหม่หรือ refresh หน้า (2026-08-27 เจอจริงจากการทดสอบ webhook เก็บเงินสำเร็จ)
          queryClient.refetchQueries({ queryKey: [ACTIVE_CREDIT_LOTS_QUERY_KEY] }),
          queryClient.refetchQueries({ queryKey: [CREDIT_TRANSACTIONS_QUERY_KEY] }),
          queryClient.refetchQueries({ queryKey: ["orderDataNewOrder"] }),
          queryClient.refetchQueries({ queryKey: ["getCompleteOrder"] }),
        ]);
        window.dispatchEvent(new Event(PARCEL_LIST_REFRESH_EVENT));
      }

      dispatch(setLastDate(new Date().toISOString()));
    });

    currentSocket.on("private-message", (msg: string) => {
      console.log("SocketIO: private-message", msg);
    });

    currentSocket.on("chat_message", (msg: any) => {
      console.log("SocketIO: chat_message", msg);
    });

    currentSocket.on("disconnect", (reason: string) => {
      console.log("SocketIO: Disconnected", reason);
    });

    return () => {
      currentSocket.off("reload");
      currentSocket.disconnect();
    };
  }, [queryClient, dispatch]);

  return (
    <SocketContext.Provider value={{ socket: socket.current }}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketProvider;
